import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../../config/api';
import { FaFileExcel, FaArrowLeft, FaSpinner } from 'react-icons/fa';
import { MEMBERSHIP_SEASON } from '../../config/membershipSeason';
import './AdminPages.css';
import './AdminMemberExport.css';

const MEMBER_ACTIVE_SEASONS = ['2024-2025', '2025-2026', '2026-2027'];

const AdminMemberExport = () => {
  const [options, setOptions] = useState([]);
  const [addressOptions, setAddressOptions] = useState([]);
  const [currentSeason, setCurrentSeason] = useState(MEMBERSHIP_SEASON);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [selectedSeason, setSelectedSeason] = useState('all');

  const standardOptions = useMemo(
    () => options.filter((option) => !addressOptions.some((item) => item.id === option.id)),
    [options, addressOptions]
  );

  const loadOptions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await api.get('/users/export-member-options');
      setOptions(Array.isArray(data.options) ? data.options : []);
      setAddressOptions(Array.isArray(data.addressExportOptions) ? data.addressExportOptions : []);
      if (data.currentSeason) {
        setCurrentSeason(data.currentSeason);
      }
    } catch (e) {
      console.error(e);
      setError("Impossible de charger la liste d'exports.");
      setOptions([]);
      setAddressOptions([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const downloadExport = async (type, { forceCurrentSeason = false } = {}) => {
    try {
      setExportingId(type);
      setError(null);
      const params = new URLSearchParams({ type });
      if (forceCurrentSeason) {
        params.set('season', currentSeason || MEMBERSHIP_SEASON);
      } else if (selectedSeason && selectedSeason !== 'all') {
        params.set('season', selectedSeason);
      }
      const response = await api.get(`/users/export-members?${params.toString()}`, {
        responseType: 'blob',
      });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const cd = response.headers['content-disposition'];
      let filename = `export-membres-${type}.xlsx`;
      if (cd) {
        const m = /filename="?([^";]+)"?/i.exec(cd);
        if (m && m[1]) filename = m[1];
      }
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      let msg = "Échec du téléchargement.";
      if (e.response?.data instanceof Blob) {
        try {
          const text = await e.response.data.text();
          const j = JSON.parse(text);
          if (j.message) msg = j.message;
          if (j.error) msg = j.error;
        } catch {
          /* ignore */
        }
      } else if (e.response?.data?.error) {
        msg = e.response.data.error;
      }
      setError(msg);
    } finally {
      setExportingId(null);
    }
  };

  return (
    <div className="admin-page admin-member-export">
      <div className="admin-container">
        <div className="admin-page-header admin-member-export-header">
          <Link to="/admin" className="admin-back-link">
            <FaArrowLeft /> Tableau de bord
          </Link>
          <div className="admin-member-export-title-row">
            <h1>
              <FaFileExcel aria-hidden /> Export membres (Excel)
            </h1>
            <div className="season-selector">
              <label htmlFor="export-season-select">Saison:</label>
              <select
                id="export-season-select"
                value={selectedSeason}
                onChange={(e) => setSelectedSeason(e.target.value)}
                disabled={!!exportingId}
              >
                <option value="all">Toutes les saisons</option>
                <option value="old">Anciennes données</option>
                {MEMBER_ACTIVE_SEASONS.map((season) => (
                  <option key={season} value={season}>
                    {season}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <p className="admin-page-intro">
            Chaque bouton génère un fichier Excel avec les mêmes colonnes que l’export des
            inscrits. Les filtres s’appliquent selon les statuts, la saison sélectionnée et les
            dates en base.
          </p>
        </div>

        {error && <div className="admin-export-error">{error}</div>}

        {loading ? (
          <p className="admin-export-loading">
            <FaSpinner className="spin" /> Chargement…
          </p>
        ) : (
          <>
            {addressOptions.length > 0 && (
              <section className="admin-export-section">
                <h2 className="admin-export-section-title">
                  Exports adresse — saison en cours ({currentSeason || MEMBERSHIP_SEASON})
                </h2>
                <ul className="admin-export-list admin-export-list-address">
                  {addressOptions.map((opt) => (
                    <li key={opt.id}>
                      <button
                        type="button"
                        className="admin-export-btn admin-export-btn-address"
                        onClick={() => downloadExport(opt.id, { forceCurrentSeason: true })}
                        disabled={!!exportingId}
                      >
                        {exportingId === opt.id ? (
                          <>
                            <FaSpinner className="spin" /> Export…
                          </>
                        ) : (
                          <>
                            <FaFileExcel aria-hidden /> {opt.label}
                          </>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="admin-export-section">
              <h2 className="admin-export-section-title">Autres exports</h2>
              <ul className="admin-export-list">
                {standardOptions.map((opt) => (
                  <li key={opt.id}>
                    <button
                      type="button"
                      className="admin-export-btn"
                      onClick={() => downloadExport(opt.id)}
                      disabled={!!exportingId}
                    >
                      {exportingId === opt.id ? (
                        <>
                          <FaSpinner className="spin" /> Export…
                        </>
                      ) : (
                        <>
                          <FaFileExcel aria-hidden /> {opt.label}
                        </>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminMemberExport;
